/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Button, Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IDXC_SubscribeNewsletterRealtimeResponse } from '../../data-actions/generated/DataServiceEntities.g';
import { subscribeEmailAction, SubscribeEmailDataActionInput } from './../../data-actions/subscribe-email/subscribe-email-data';
import { ICittaUnsubscribeData } from './citta-unsubscribe.data';
import { ICittaUnsubscribeProps } from './citta-unsubscribe.props.autogenerated';

export interface ICittaUnsubscribeViewProps extends ICittaUnsubscribeProps<ICittaUnsubscribeData> {
    unsubscribeContainer: IModuleProps;
    alertMessage?: React.ReactNode;
    unsubscribeHeading?: React.ReactNode;
    unsubscribeInput?: React.ReactNode;
    unsubscribeButton?: React.ReactNode ;
}

interface ICittaUnsubscribeState {
    alertMessage: string;
    alertClass: string;
    inputEmail: string;
}

/**
 *
 * CittaUnsubscribe component
 * @extends {React.PureComponent<ICittaUnsubscribeProps<ICittaUnsubscribeData>>}
 */
class CittaUnsubscribe extends React.PureComponent<ICittaUnsubscribeProps<ICittaUnsubscribeData>, ICittaUnsubscribeState> {
    constructor(props: ICittaUnsubscribeViewProps) {
        super(props);
        this._handleButtonClick = this._handleButtonClick.bind(this);
        this._handleInputChange = this._handleInputChange.bind(this);

        this.state = {
            alertMessage: '',
            alertClass: '',
            inputEmail: ''
        };
    }

    public render(): JSX.Element | null {
        const viewProps: ICittaUnsubscribeViewProps = {
            ...(this.props as ICittaUnsubscribeProps<ICittaUnsubscribeData>),
            unsubscribeContainer: {
                moduleProps: this.props,
                className: 'container citta-unsubscribe'
            },
            alertMessage: this._renderAlertMessage(),
            unsubscribeHeading: this._renderUnsubscribeHeading(),
            unsubscribeInput: this._renderUnsubscribeInput(),
            unsubscribeButton: this._renderUnsubscribeButton()
        };
        return this.props.renderView(viewProps);
    }

    // tslint:disable-next-line: no-any
    private _handleInputChange = (event: any) => {
        this.setState({
            inputEmail: event.target.value
        });
    }

    private _handleButtonClick = async () => {
        const response = await this._callUnsubscribeAPI(this.state.inputEmail);
        if (response.Result && response.Message) {
            this.setState({
                alertMessage: `${response.Message}`,
                alertClass: response.Message === 'Unsubscribed successfully.' ? 'alert-green' : 'alert-yellow'
            });
        }
    }

    private async _callUnsubscribeAPI(email: string): Promise<IDXC_SubscribeNewsletterRealtimeResponse> {
        try {
            return await subscribeEmailAction(new SubscribeEmailDataActionInput(email, false), this.props.context.actionContext);
        } catch (e) {
            throw this.props.telemetry.error(`Something went wrong ------${e}`);
        }
    }

    private _renderUnsubscribeHeading(): React.ReactNode {
        const { unsubscribeHeading } = this.props.config;
        return (
            <Heading className='ms-content-block__title' headingTag={unsubscribeHeading!.tag} text={unsubscribeHeading!.text} />
        );
    }

    private _renderAlertMessage(): React.ReactNode {
        const { alertMessage, alertClass } = this.state;
        const displayAlert: boolean = (alertMessage ? alertMessage !== '' : false);
        return (
            <>
                {displayAlert ? <div className='unsubscribe-alert'>
                    <p className={alertClass}>{alertMessage}</p>
                </div> : null}
            </>
        );
    }

    private _renderUnsubscribeInput(): React.ReactNode {
        return (
            <div className='unsubscribe-input'>
                <input id='emailInput' onChange={this._handleInputChange} type='email' className='form-control' placeholder='Enter your email...' />
            </div>
        );
    }

    private _renderUnsubscribeButton(): React.ReactNode {
        const { resources } = this.props;
        return (
            <Button
                className='unsubscribe-button'
                title={resources.title}
                color='primary'
                onClick={this._handleButtonClick}
            >{resources.buttonText}
            </Button>
        );
    }
}

export default CittaUnsubscribe;